import cuid from "cuid";
import { find, get, groupBy, union } from "lodash";
import omitDeep from "omit-deep-lodash";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { MdDescription, MdDownload, MdInfo } from "react-icons/md";
import InfoBox from "screens/Private/Broker/Projects/Detail/Templates/Manual/Lafourgale/Components/InfoBox";
import Button from "shared/components/Button";
import { NAME_SPACES } from "shared/locales/constants";
import { CONTACT_POINT_SYSTEM, DOCUMENTS_TYPES } from "utils/constants";
import { downloadDocument } from "utils/helpers/files";
import ActionButtons from "../../../../../../Projects/Detail/Templates/Manual/Lafourgale/Components/ActionButtons";

const FILE_MODE = {
  SIMPLE_FILE: "SIMPLE_FILE",
  GENERATE: "GENERATE",
};

const View = ({
  attachments,
  onBack,
  totalPrice,
  subscription,
  onSign,
  downloadAfterGenerate,
  downloadSingleDocument,
  loading,
  setLoading,
}) => {
  const { t } = useTranslation(NAME_SPACES.PRIVATE.BROKER.CONTRACT);
  const DOCUMENTS = t("SUBSCRIPTION.DOCUMENT.DOCUMENTS", {
    returnObjects: true,
  });

  const download = (file) => {
    const { id, generate } = file;
    if (generate) return file.onClick();
    setLoading((prev) => ({ ...prev, [id]: true }));
    downloadSingleDocument({
      variables: { where: { id } },
      onCompleted: (_) => {
        const { document } = omitDeep(_.downloadSingleDocument, "__typename");
        downloadDocument(document);
        setLoading((prev) => ({ ...prev, [id]: false }));
      },
    });
  };
  const SECTIONS = useMemo(() => {
    const {
      DUTY_OF_ADVICE_QUOTE = [],
      CONSENT_FORM = [],
      DUTY_OF_ADVICE = [],
      MEMBERSHIP_FORM = [],
      SEPA_DIRECT_DEBIT_MANDATE = [],
      RGPD = [],
      IPID = [],
      INFO_NOTICE = [],
      BROCHURE = [],
      STATUSES = [],
      QUOTE = [],
    } = groupBy(union(attachments, subscription?.attachments), "type");

    return [
      {
        title: DOCUMENTS.MEMBERSHIP_APPLICATION,
        isChecked: true,
        files: [
          ...(subscription.locked && MEMBERSHIP_FORM.length
            ? MEMBERSHIP_FORM.map(({ name, ...rest }) => ({
                ...rest,
                filename: name,
                mode: FILE_MODE.SIMPLE_FILE,
              }))
            : [
                {
                  id: cuid(),
                  filename: "BULLETIN D'ADHÉSION",
                  mode: FILE_MODE.GENERATE,
                  generate: true,
                  onClick: () =>
                    downloadAfterGenerate(DOCUMENTS_TYPES.MEMBERSHIP_FORM),
                },
              ]),
          ...(subscription.locked && DUTY_OF_ADVICE.length
            ? DUTY_OF_ADVICE.map(({ name, ...rest }) => ({
                ...rest,
                filename: name,
                mode: FILE_MODE.SIMPLE_FILE,
              }))
            : [
                {
                  id: cuid(),
                  filename: "Devoir de conseil",
                  mode: FILE_MODE.GENERATE,
                  generate: true,
                  onClick: () =>
                    downloadAfterGenerate(DOCUMENTS_TYPES.DUTY_OF_ADVICE),
                },
              ]),
          ...(subscription.locked && CONSENT_FORM.length
            ? CONSENT_FORM.map(({ name, ...rest }) => ({
                ...rest,
                filename: name,
                mode: FILE_MODE.SIMPLE_FILE,
              }))
            : [
                {
                  id: cuid(),
                  filename: "Poped Liasse",
                  mode: FILE_MODE.GENERATE,
                  generate: true,
                  onClick: () =>
                    downloadAfterGenerate(DOCUMENTS_TYPES.CONSENT_FORM),
                },
              ]),
          ...(subscription.locked && SEPA_DIRECT_DEBIT_MANDATE.length
            ? SEPA_DIRECT_DEBIT_MANDATE.map(({ name, ...rest }) => ({
                ...rest,
                filename: name,
                mode: FILE_MODE.SIMPLE_FILE,
              }))
            : [
                {
                  id: cuid(),
                  filename: "Mandat SEPA",
                  mode: FILE_MODE.GENERATE,
                  generate: true,
                  onClick: () =>
                    downloadAfterGenerate(
                      DOCUMENTS_TYPES.SEPA_DIRECT_DEBIT_MANDATE,
                    ),
                },
              ]),
        ],
      },
      {
        title: DOCUMENTS.QUOTES,
        files: [
          ...DUTY_OF_ADVICE_QUOTE.map(({ name, ...rest }) => ({
            ...rest,
            filename: name,
          })),
          ...QUOTE.map(({ name, ...rest }) => ({
            ...rest,
            filename: name,
          })),
        ],
      },
      {
        title: DOCUMENTS.NOTICES,
        files: [
          ...INFO_NOTICE.map(({ name, ...rest }) => ({
            ...rest,
            filename: name,
          })),
          ...IPID.map(({ name, ...rest }) => ({
            ...rest,
            filename: name,
          })),
          ...BROCHURE.map(({ name, ...rest }) => ({
            ...rest,
            filename: name,
          })),
          ...STATUSES.map(({ name, ...rest }) => ({
            ...rest,
            filename: name,
          })),
          ...RGPD.map(({ name, ...rest }) => ({
            ...rest,
            filename: name,
          })),
        ],
      },
    ].filter(Boolean);
  }, [subscription]);

  const telecoms = get(subscription, "project.contact.telecoms", []);
  const email = get(
    find(telecoms, { system: CONTACT_POINT_SYSTEM.EMAIL }),
    "value",
    "",
  );
  return (
    <div className="contact-form lf-subscription" key="form_area">
      <div className="lf-subscription__container">
        <div className="lf-subscription__container--header">
          <strong>Documents et Signature</strong>
          <p>
            Phase 6: Génération des documents contractuels et signature
            électronique
          </p>
        </div>
        <InfoBox
          icon={<MdInfo size={16} />}
          content={
            <>
              <strong>Signature électronique eIDAS</strong>
              <p>
                Les documents ci-dessous ont été générés automatiquement et sont
                prêts pour la signature électronique. La signature sera conforme
                au règlement eIDAS et aura la même valeur juridique qu'une
                signature manuscrite.
              </p>
            </>
          }
        />
        <div className="lf-subscription__container--content mb-32"></div>
        <div className="lf-subscription__container--content mb-32">
          {attachments.map((doc) => (
            <div key={doc.file} className="lf-subscription_document">
              <div className="lf-subscription_document--header">
                <div className="lf-subscription_document--header--titles">
                  <span className="lf-subscription_document--header--titles__icon">
                    <MdDescription size={18} />
                  </span>
                  <div className="lf-subscription_document--header--titles__title">
                    <b>{doc.name}</b>
                    <span>{doc.name}</span>
                  </div>
                </div>
                <Button type="gray--outlined" onClick={() => download(doc)}>
                  <MdDownload size={16} />
                  Télécharger
                </Button>
              </div>
              <span className="text-sm">{doc.file}</span>
            </div>
          ))}
        </div>
        <div className="lf-subscription__container--content mb-32">
          <b>Récapitulatif final</b>
        </div>
        <div className="lf-subscription__container--content mb-32">
          <div className="contact-stepper__content">
            <div className="d-flex justify--between">
              <span>Client:</span>
              <span>
                {get(subscription, "project.contact.user.firstname", "")}{" "}
                {get(subscription, "project.contact.user.lastname", "")}
              </span>
            </div>
            <div className="d-flex justify--between">
              <span>Email:</span>
              <span>{email}</span>
            </div>
            <div className="d-flex justify--between">
              <span>Formule téléconsultation:</span>
              <span>{get(subscription, "insurancePlan.name")}</span>
            </div>
            <div className="d-flex justify--between">
              <span>Montant mensuel:</span>
              <strong className="text--primary">
                {totalPrice.toFixed(2)} €
              </strong>
            </div>
            <div className="d-flex justify--between">
              <span>Nombre de documents:</span>
              <span>{attachments.length}</span>
            </div>
          </div>
        </div>
        <ActionButtons
          onBack={onBack}
          onSubmit={onSign}
          loading={loading.sign}
          translation={{
            NEXT: "Signer électroniquement",
            PREVIOUS: "Retour",
          }}
        />
      </div>
    </div>
  );
};

export default View;
